// src/app/components/manage-employees-per-org/manage-employees-per-org.component.ts
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {
  Firestore,
  collection,
  collectionData,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
} from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FirebaseService } from 'src/app/services/firebase.service';

declare var bootstrap: any;

interface Schedule {
  id?: string;
  orgId: string;
  title: string;
  weekStart: string;
  weekEnd: string;
  createdAt: Date;
  createdBy?: string;
  result?: any[];
}

interface Employee {
  id?: string;
  Name: string;
  Pin: string;
  orgId: string;
  keyHolder: boolean;
  fullTime: boolean;
  cashier?: boolean;
  notes?: string;
  availability?: { [key: string]: string };
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  password?: string;
  requestedOff?: Array<{
    date: string;
    status: string;
    approvedBy?: string;
  }>;
  incompleteAvailability?: boolean;
  incompleteFullName?: boolean;
  incompletePassword?: boolean;
}

@Component({
  selector: 'app-manage-employees-per-org',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './manage-employees-per-org.component.html',
  styleUrls: ['./manage-employees-per-org.component.css'],
})
export class ManageEmployeesPerOrgComponent implements OnInit, OnDestroy {
  private readonly firestore = inject(Firestore);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  constructor(private firebase: FirebaseService) {}

  readonly dayKeys = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  // ============================================
  // TIME FORMAT VALIDATOR - FULLY FLEXIBLE
  // ============================================
  private readonly timeFormatValidator = (control: any) => {
    const value = control.value?.trim().toLowerCase();

    // Allow "off" or empty
    if (!value || value === 'off') {
      return null;
    }

    // Accepts ANY minutes from 00-59
    // Examples: "9:00 AM - 5:00 PM", "9:15 AM - 5:45 PM", "10:30 AM - 6:30 PM"
    const timePattern =
      /^(1[0-2]|0?[1-9]):[0-5][0-9]\s?(AM|PM)\s?-\s?(1[0-2]|0?[1-9]):[0-5][0-9]\s?(AM|PM)$/i;

    if (!timePattern.test(value)) {
      return { invalidTimeFormat: true };
    }

    return null;
  };

  // Forms
  readonly addEmployeeForm = this.fb.nonNullable.group({
    Name: ['', Validators.required],
    Pin: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    keyHolder: [false],
    fullTime: [false],
    cashier: [false],
  });

  readonly editEmployeeForm = this.fb.nonNullable.group({
    Name: ['', Validators.required],
    Pin: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    fullTime: [false],
    keyHolder: [false],
    cashier: [false],
    notes: [''],
    Sunday: ['off', this.timeFormatValidator],
    Monday: ['off', this.timeFormatValidator],
    Tuesday: ['off', this.timeFormatValidator],
    Wednesday: ['off', this.timeFormatValidator],
    Thursday: ['off', this.timeFormatValidator],
    Friday: ['off', this.timeFormatValidator],
    Saturday: ['off', this.timeFormatValidator],
  });

  // State
  employees: Employee[] = [];
  selectedEmployee: Employee | null = null;
  isLoading = true;
  isSubmitting = false;
  userOrgId: string | null = null;
  organizationName = 'Your Organization';

  // Toast
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  orgId: string | null = null;
  orgName: string = '';
  readonly cashierBackfillOrgId = 'platoscloset555';
  backfillingCashier = false;
  canRunCashierBackfill = false;

  async ngOnInit() {
    const currentUser = this.auth.getUser();
    this.canRunCashierBackfill =
      (currentUser?.role || '').toLowerCase() === 'admin';
    this.initializeUsersOrg();
    this.initializeComponent();
  }

  async initializeUsersOrg() {
    this.orgId = this.auth.getUsersOrgId();
    if (!this.orgId) return;

    const orgData = await this.firebase.getOrganizationById(this.orgId);
    this.orgName = orgData?.name || 'Unknown Organization';

    this.employees = await this.firebase.getEmployeesByOrgId(this.orgId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  intializeUsersOrg() {
    this.orgId = this.auth.getUsersOrgId();
  }

  private async initializeComponent(): Promise<void> {
    const user = this.auth.getUser();

    if (!user || !user.orgId) {
      this.showToast('No organization found. Please log in again.', 'error');
      setTimeout(() => this.router.navigate(['/login']), 2000);
      return;
    }

    this.userOrgId = user.orgId;

    await this.loadEmployees();
  }

  private async loadEmployees(): Promise<void> {
    if (!this.userOrgId) return;

    this.isLoading = true;
    try {
      const empRef = collection(this.firestore, 'Employees');
      const q = query(empRef, where('orgId', '==', this.userOrgId));

      collectionData(q, { idField: 'id' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.employees = (data as Employee[])
              .map((emp) => {
                const availability = emp.availability || {};
                const allOff = Object.values(availability).every(
                  (v) => String(v || '').toLowerCase() === 'off',
                );

                const hasIncompleteFullName = !emp.firstName || !emp.lastName;
                const hasIncompletePassword =
                  !emp.password || emp.password.trim() === '';

                return {
                  ...emp,
                  incompleteAvailability: allOff,
                  incompleteFullName: hasIncompleteFullName,
                  incompletePassword: hasIncompletePassword,
                };
              })
              .sort((a, b) => a.Name.localeCompare(b.Name));
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error loading employees:', error);
            this.showToast('Failed to load employees', 'error');
            this.isLoading = false;
          },
        });
    } catch (error) {
      console.error('Error setting up employee listener:', error);
      this.showToast('Failed to load employees', 'error');
      this.isLoading = false;
    }
  }

  async onAddEmployeeSubmit(): Promise<void> {
    if (this.addEmployeeForm.invalid || !this.userOrgId) return;

    this.isSubmitting = true;
    try {
      const employeeData: Omit<Employee, 'id'> = {
        ...this.addEmployeeForm.getRawValue(),
        orgId: this.userOrgId,
        role: 'employee',
        availability: Object.fromEntries(this.dayKeys.map((d) => [d, 'off'])),
        cashier: this.addEmployeeForm.getRawValue().cashier ?? false,
      };

      const employeesRef = collection(this.firestore, 'Employees');
      await addDoc(employeesRef, employeeData);

      this.showToast('Employee added successfully!', 'success');
      this.addEmployeeForm.reset({
        keyHolder: false,
        fullTime: false,
        cashier: false,
      });

      const modal = bootstrap.Modal.getInstance('#addEmployeeModal');
      modal?.hide();
    } catch (error) {
      console.error('Error adding employee:', error);
      this.showToast('Failed to add employee', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  openEditOffcanvas(emp: Employee): void {
    this.selectedEmployee = emp;

    const offcanvasElement = document.getElementById('editEmployeeOffcanvas');
    if (offcanvasElement) {
      const offcanvas =
        bootstrap.Offcanvas.getInstance(offcanvasElement) ||
        new bootstrap.Offcanvas(offcanvasElement);
      offcanvas.show();
    }

    this.editEmployeeForm.patchValue({
      Name: emp.Name,
      Pin: emp.Pin,
      fullTime: emp.fullTime,
      keyHolder: emp.keyHolder,
      cashier: emp.cashier ?? false,
      notes: emp.notes || '',
      ...(emp.availability || {}),
    });
  }

  async onEditEmployeeSubmit(): Promise<void> {
    if (!this.selectedEmployee || this.editEmployeeForm.invalid) return;

    const formValue = this.editEmployeeForm.getRawValue();
    const empRef = doc(this.firestore, 'Employees', this.selectedEmployee.id!);

    const updatedData = {
      Name: formValue.Name,
      Pin: formValue.Pin,
      fullTime: formValue.fullTime,
      keyHolder: formValue.keyHolder,
      cashier: formValue.cashier ?? false,
      notes: formValue.notes,
      availability: Object.fromEntries(
        this.dayKeys.map((d) => [d, (formValue as any)[d] || 'off']),
      ),
    };

    try {
      await updateDoc(empRef, updatedData);
      this.showToast(`Updated ${formValue.Name} successfully!`, 'success');

      const offcanvas = bootstrap.Offcanvas.getInstance(
        '#editEmployeeOffcanvas',
      );
      offcanvas?.hide();
    } catch (error) {
      console.error('Error updating employee:', error);
      this.showToast('Failed to update employee', 'error');
    }
  }

  async deleteEmployee(emp: Employee): Promise<void> {
    if (!emp.id) return;

    const confirmDelete = confirm(
      `Are you sure you want to delete ${emp.Name}? This action cannot be undone.`,
    );

    if (!confirmDelete) return;

    try {
      const empRef = doc(this.firestore, 'Employees', emp.id);
      await deleteDoc(empRef);
      this.showToast(`Deleted ${emp.Name} successfully!`, 'success');
    } catch (error) {
      console.error('Error deleting employee:', error);
      this.showToast('Failed to delete employee', 'error');
    }
  }

  async runCashierBackfill(): Promise<void> {
    if (!this.canRunCashierBackfill) {
      this.showToast('Only admins can run this update.', 'error');
      return;
    }

    if (
      !confirm(
        `Run one-time cashier backfill for org \"${this.cashierBackfillOrgId}\"? This only updates employees missing the cashier field.`,
      )
    ) {
      return;
    }

    this.backfillingCashier = true;
    try {
      const result = await this.firebase.backfillMissingCashierForOrg(
        this.cashierBackfillOrgId,
      );

      this.showToast(
        `Cashier update complete. Updated ${result.updated}, skipped ${result.skipped}, total ${result.total}.`,
        'success',
      );
    } catch (error) {
      console.error('Error running cashier backfill:', error);
      this.showToast('Failed to run cashier update.', 'error');
    } finally {
      this.backfillingCashier = false;
    }
  }

  // ============================================
  // TIME FORMAT HELPER METHODS
  // ============================================

  formatTimeInput(day: string): void {
    const control = this.editEmployeeForm.get(day);
    if (!control) return;

    let value = control.value?.trim();

    // If empty or "off", set to "off"
    if (!value || value.toLowerCase() === 'off') {
      control.setValue('off');
      return;
    }

    // Auto-format the input
    value = value
      .toUpperCase()
      .replace(/\s+/g, ' ') // Remove extra spaces
      .replace(/\s*-\s*/g, ' - ') // Fix spacing around dash
      .replace(/(\d)(AM|PM)/gi, '$1 $2'); // Add space before AM/PM

    control.setValue(value);
    control.updateValueAndValidity();
  }

  hasTimeError(day: string): boolean {
    const control = this.editEmployeeForm.get(day);
    return !!(
      control?.invalid &&
      control?.touched &&
      control?.errors?.['invalidTimeFormat']
    );
  }

  setDayOff(day: string): void {
    this.editEmployeeForm.get(day)?.setValue('off');
    this.editEmployeeForm.get(day)?.markAsTouched();
  }

  // ============================================
  // TOAST & NAVIGATION
  // ============================================

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    setTimeout(() => (this.toastVisible = false), 3000);
  }

  closeToast(): void {
    this.toastVisible = false;
  }

  goBack(): void {
    this.router.navigate(['/manager']);
  }
}
